import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { authService } from '@/services/auth';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await authService.requestPasswordReset(email.trim());
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot password - Arrangelly</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-gradient-to-br from-slate-800/80 to-purple-900/20 border border-white/10 rounded-2xl p-8 backdrop-blur-md">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Forgot password</h1>
          <p className="text-sm text-gray-400 mb-6">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>
          {done ? (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-lg text-sm">
                If an account exists for that email, we&apos;ve sent instructions. Check your inbox and spam
                folder.
              </div>
              <Link
                to="/login"
                className="inline-block text-purple-300 hover:text-purple-200 underline text-sm"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full rounded-lg bg-slate-900/60 border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="you@example.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full mt-2 bg-accent hover:bg-accent/90 text-white disabled:opacity-50"
                >
                  {busy ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
              <p className="mt-6 text-xs text-gray-400 text-center">
                <Link to="/login" className="text-purple-300 hover:text-purple-200 underline">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
